import { logger } from '@/lib/logger';
import { CallEventType, useCallEventStore } from '@stores/call/callEventStore';
import { useCallReconnectStore } from '@stores/call/callReconnectStore';
import { useCallStore } from '@stores/call/callStore';
import { type SpeakingEvent } from '@stores/call/callTypes';
import { useDataConnectionsStore } from '@stores/call/dataConnectionsStore';
import { useLocalMediaStore } from '@stores/call/localMediaStore';
import { useQueryClient } from '@tanstack/vue-query';
import { usersQueries, usersKeys } from '@therame/api-client';
import { UserProfileDto } from '@therame/shared';
import hark from 'hark';
import Peer, { type MediaConnection } from 'peerjs';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';
import { computed, ref, type ComputedRef, type DeepReadonly, type Ref } from 'vue';

type SafeSubject<T> = Omit<Subject<T>, 'currentObservers'>;

type SafeMediaConnection = DeepReadonly<
  Pick<
    MediaConnection,
    | 'peer'
    | 'label'
    | 'type'
    | 'localStream'
    | 'remoteStream'
    | 'peerConnection'
    | 'answer'
    | 'close'
    | 'on'
    | 'removeAllListeners'
  >
>;

type MediaConnectionsStore = {
  createNewMediaConnection: (
    peerJs: Peer,
    userId: string,
    localMediaStream: MediaStream,
  ) => Promise<boolean>;
  receiveMediaConnection: (
    mediaConnection: MediaConnection,
    localMediaStream: MediaStream,
  ) => Promise<boolean>;
  remoteStreams: ComputedRef<Array<[string, MediaStream]>>;
  remoteStreamsMap: Ref<Map<string, MediaStream>>;
  remoteStreamSourcesMap: Ref<Map<string, MediaStreamAudioSourceNode>>;
  mediaConnections: Ref<Map<string, SafeMediaConnection>>;
  lockedFocusUserId: Ref<string | undefined>;
  focusedRemoteStream: Ref<MediaStream | undefined>;
  focusedUserId: Ref<string | undefined>;
  updateFocusedStream: (options: {
    stream: MediaStream;
    userId: string;
    init?: boolean;
  }) => Promise<void>;
  updateFocusedStreamOnClose: (userId: string) => void;
  speakingEvents: Ref<SafeSubject<SpeakingEvent>>;
  focusedRemoteStreamChange: Ref<SafeSubject<MediaStream | undefined>>;
  remoteAudioElements: Ref<Map<string, HTMLAudioElement>>;
};

export const useMediaConnectionsStore = defineStore(
  'mediaConnections',
  (): MediaConnectionsStore => {
    const callStore = useCallStore();
    const callEventStore = useCallEventStore();
    const localMediaStore = useLocalMediaStore();
    const dataConnectionsStore = useDataConnectionsStore();
    const callReconnectStore = useCallReconnectStore();
    const queryClient = useQueryClient();

    const mediaConnections = ref<Map<string, SafeMediaConnection>>(
      new Map<string, SafeMediaConnection>(),
    );
    const remoteStreamsMap = ref<Map<string, MediaStream>>(new Map<string, MediaStream>());
    const remoteStreamSourcesMap = ref<Map<string, MediaStreamAudioSourceNode>>(
      new Map<string, MediaStreamAudioSourceNode>(),
    );

    const remoteAudioElements = ref<Map<string, HTMLAudioElement>>(new Map());

    const focusedRemoteStream = ref<MediaStream | undefined>(undefined);
    const focusedRemoteStreamChange = ref<Subject<MediaStream | undefined>>(
      new Subject<MediaStream | undefined>(),
    );
    const focusedUserId = ref<string | undefined>(undefined);
    const speakingEvents = ref<SafeSubject<SpeakingEvent>>(new Subject<SpeakingEvent>());
    const lockedFocusUserId = ref<string | undefined>(undefined);

    const remoteStreams = computed(() => {
      return Array.from(remoteStreamsMap.value);
    });

    /**
     *  - handles listeners for a media connection
     */
    const handleListenersForMediaConnection = (mediaConnection: MediaConnection) => {
      mediaConnection.on('stream', async (stream) => {
        callEventStore.emit(CallEventType.P2P_MEDIA_CONNECTION, 'stream', { stream });
        const remoteUserId = mediaConnection.peer;
        remoteStreamsMap.value.set(remoteUserId, stream);
        playRemoteAudioStream(remoteUserId, stream);
        if (!stream.getAudioTracks()[0]) {
          logger.debug({ msg: 'remote stream without audio' });
        }
        if (!stream.getVideoTracks()[0]) {
          logger.debug({ msg: 'remote stream without video' });
        }
        await updateFocusedStream({
          stream,
          userId: remoteUserId,
          init: true,
        });
        addStreamSpeechDetection(stream, remoteUserId);
      });
      mediaConnection.on('error', async (error) => {
        callEventStore.emit(CallEventType.P2P_MEDIA_CONNECTION, 'error', { error });
        callStore.disconnectPeer(mediaConnection.peer);
        callReconnectStore.startReconnectingToPeer(mediaConnection.peer);
      });
      mediaConnection.on('close', () => {
        callEventStore.emit(CallEventType.P2P_MEDIA_CONNECTION, 'close', {});
        callStore.disconnectPeer(mediaConnection.peer);
      });
    };

    /**
     *  - stores info for media-connection in state
     *  - handles incomming mediaStream
     *  - listens for connection close and updates local state accordingly
     */
    const createNewMediaConnection = async (
      peerJs: Peer,
      userId: string,
      localMediaStream: MediaStream,
    ) => {
      return Promise.race<boolean>([
        new Promise((resolve) => {
          const mediaConnection: MediaConnection = peerJs.call(userId, localMediaStream);
          localMediaStore.updateMuteStates();
          mediaConnections.value.set(mediaConnection.peer, mediaConnection);
          handleListenersForMediaConnection(mediaConnection);
          resolve(true);
        }),
        new Promise((resolve) => {
          setTimeout(() => {
            resolve(false);
          });
        }),
      ]);
    };

    /**
     *  - stores info for media-connection in state
     *  - handles incomming mediaStream
     *  - listens for connection close and updates local state accordingly
     */
    const receiveMediaConnection = async (
      mediaConnection: MediaConnection,
      localMediaStream: MediaStream,
    ) => {
      return Promise.race<boolean>([
        new Promise((resolve) => {
          mediaConnection.answer(localMediaStream);
          localMediaStore.updateMuteStates();
          mediaConnections.value.set(mediaConnection.peer, mediaConnection);
          handleListenersForMediaConnection(mediaConnection);
          resolve(true);
        }),
        new Promise((resolve) => {
          setTimeout(() => {
            resolve(false);
          });
        }),
      ]);
    };

    /**
     * Creates (or reuses) an HTMLAudioElement for a given remote stream
     * and attaches the stream to it.
     */
    const playRemoteAudioStream = (remoteUserId: string, stream: MediaStream) => {
      let audioElement = remoteAudioElements.value.get(remoteUserId);
      if (!audioElement) {
        audioElement = document.createElement('audio');
        audioElement.id = `remoteAudio_${remoteUserId}`;
        audioElement.autoplay = true;
        (audioElement as any).playsInline = true;
        audioElement.style.display = 'none';
        document.body.appendChild(audioElement);
        remoteAudioElements.value.set(remoteUserId, audioElement);
      }
      audioElement.srcObject = stream;
      audioElement
        .play()
        .catch((err) =>
          logger.error({ msg: `Error playing remote audio for ${remoteUserId}`, error: err }),
        );
    };

    const addStreamSpeechDetection = (stream: MediaStream, userId: string) => {
      if (stream.getAudioTracks().length > 0) {
        const speechEvents = hark(stream);
        speechEvents.setInterval(200);

        // set the focused stream if the user is speaking
        speechEvents.on('speaking', async function () {
          speakingEvents.value.next({
            userId: userId,
            active: true,
          });
          updateFocusedStream({
            stream,
            userId: userId,
          });
        });

        speechEvents.on('stopped_speaking', function () {
          speakingEvents.value.next({
            userId: userId,
            active: false,
          });
        });
      }
    };

    /** set focused stream if not in focus-mode and user has cam enabled */
    const updateFocusedStream = async (options: {
      stream: MediaStream;
      userId: string;
      init?: boolean;
    }) => {
      if (
        options.init ||
        (!lockedFocusUserId.value && focusedRemoteStream.value?.id !== options.stream?.id)
      ) {
        focusedRemoteStream.value = options.stream;
        focusedUserId.value = options.userId;
        dataConnectionsStore.focusedCallMetadata = dataConnectionsStore.remoteCallMetadata.get(
          options.userId,
        ) || {
          audioEnabled: false,
          videoEnabled: false,
        };
        focusedRemoteStreamChange.value.next(options.stream);
        setConnectionTextForActiveUser(options.userId);
      }
    };

    const updateFocusedStreamOnClose = (userId: string) => {
      if (focusedUserId.value === userId) {
        // show empty if.value last remote stream dropped or show a random remaining stream
        if (remoteStreamsMap.value.size === 0) {
          focusedRemoteStream.value = undefined;
          callStore.connectionText = 'Warte auf Teilnehmer:innen...';
          clearInterval(callStore.callDurationTimerInterval);
          callStore.callDurationTimerInterval = undefined;
        } else {
          const streams = Array.from(remoteStreamsMap.value.values());
          const userIds = Array.from(remoteStreamsMap.value.keys());
          const newIndex = Math.floor(Math.random() * streams.length);
          focusedRemoteStream.value = streams[newIndex];
          focusedRemoteStreamChange.value.next(streams[newIndex]);
          focusedUserId.value = userIds[newIndex];
          setConnectionTextForActiveUser(userIds[newIndex]);
        }
      }
    };

    const setConnectionTextForActiveUser = async (userId: string) => {
      const metadata = dataConnectionsStore.remoteCallMetadata.get(userId);
      if (metadata?.displayName) {
        callStore.connectionText = metadata.displayName;
      } else {
        const remoteUserId = userId;
        const profile = await getUserProfile(remoteUserId);
        callStore.connectionText = profile.firstName + ' ' + profile.lastName;
      }
    };

    const getUserProfile = async (remoteUserId: string): Promise<UserProfileDto> => {
      return queryClient.fetchQuery({
        queryKey: usersKeys.profile(remoteUserId),
        queryFn: () => usersQueries.getProfile(remoteUserId),
      });
    };

    const store: MediaConnectionsStore = {
      createNewMediaConnection,
      receiveMediaConnection,
      remoteStreams,
      remoteStreamsMap,
      remoteStreamSourcesMap,
      mediaConnections,
      lockedFocusUserId,
      focusedRemoteStream,
      focusedUserId,
      updateFocusedStream,
      updateFocusedStreamOnClose,
      speakingEvents,
      focusedRemoteStreamChange,
      remoteAudioElements,
    };
    return store;
  },
);
